import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { scoringApiService } from '../services/scoringApi';
import {
  BcAttemptState,
  type BcAttempt,
  type BcPhoneNumber,
  type ScoringInterrogationAttempt,
  type ScoringState,
} from '../types/scoring';

export const ScoringQueryKeys = {
  scoringState: 'scoring-state',
  bcPhones: 'bc-phones',
};

interface UseScoringReturn {
  scoringState: ScoringState | undefined;
  isScoringStateLoading: boolean;
  bcPhones: BcPhoneNumber[] | undefined;
  isBcPhonesLoading: boolean;
  initiateScoreObtaining: () => Promise<ScoringInterrogationAttempt>;
  isInitiating: boolean;
  sendVerificationCode: (phoneId: string) => Promise<BcAttempt>;
  isSendingCode: boolean;
  generateByCode: (code: string) => Promise<BcAttempt>;
  isGeneratingByCode: boolean;
  refreshScoringState: () => Promise<void>;
}

interface UseScoringOptions {
  fetchScoringState?: boolean;
  fetchBcPhones?: boolean;
}

/**
 * Credit bureau (BC) score, obtained without KYC by confirming a phone number
 * registered at the bureau. Scoring itself runs in the background: a completed
 * attempt does not mean the score is ready yet.
 */
export const useScoring = ({
  fetchScoringState = false,
  fetchBcPhones = false,
}: UseScoringOptions = {}): UseScoringReturn => {
  const queryClient = useQueryClient();

  const scoringStateQuery = useQuery({
    queryKey: [ScoringQueryKeys.scoringState],
    queryFn: () => scoringApiService.getScoringState(),
    enabled: fetchScoringState,
  });

  const bcPhonesQuery = useQuery({
    queryKey: [ScoringQueryKeys.bcPhones],
    queryFn: () => scoringApiService.getBcPhones(),
    enabled: fetchBcPhones,
  });

  const refreshScoringState = () =>
    queryClient.invalidateQueries({
      queryKey: [ScoringQueryKeys.scoringState],
    });

  const initiateMutation = useMutation({
    mutationFn: () => scoringApiService.initiateScoreObtaining(),
    onSuccess: refreshScoringState,
  });

  const sendVerificationCodeMutation = useMutation({
    mutationFn: (phoneId: string) =>
      scoringApiService.sendVerificationCode(phoneId),
  });

  const generateByCodeMutation = useMutation({
    mutationFn: (code: string) => scoringApiService.generateByCode(code),
    onSuccess: (attempt) => {
      if (attempt.bcAttemptState === BcAttemptState.Completed) {
        return refreshScoringState();
      }
    },
  });

  return {
    scoringState: scoringStateQuery.data,
    isScoringStateLoading: scoringStateQuery.isLoading,
    bcPhones: bcPhonesQuery.data,
    isBcPhonesLoading: bcPhonesQuery.isLoading,
    initiateScoreObtaining: initiateMutation.mutateAsync,
    isInitiating: initiateMutation.isPending,
    sendVerificationCode: sendVerificationCodeMutation.mutateAsync,
    isSendingCode: sendVerificationCodeMutation.isPending,
    generateByCode: generateByCodeMutation.mutateAsync,
    isGeneratingByCode: generateByCodeMutation.isPending,
    refreshScoringState,
  };
};
